/*
 * @Author: Ryan
 * @Date: 2025-12-29
 * @Description: Frequently Asked Questions section with expandable items
 */
import React, { useState } from 'react';

interface FAQItem {
  id: string;
  question: string;
  answer: string | React.ReactNode;
}

interface FAQItemProps {
  item: FAQItem;
  isExpanded: boolean;
  onClick: () => void;
}

const FAQItemComponent: React.FC<FAQItemProps> = ({ item, isExpanded, onClick }) => {
  return (
    <div
      className={`
        border border-[#CDCDCD] transition-all duration-300 cursor-pointer
        ${isExpanded ? 'bg-white' : 'bg-white hover:bg-gray-50'}
      `}
      onClick={onClick}
    >
      <div className="px-6 py-6 pad:px-8 pad:py-8">
        {/* 问题行 */}
        <div className="flex items-center justify-between gap-4">
          {/* 问题文本 */}
          <h3 className="flex-1 text-base pad:text-base font-semibold text-[#0F1117]">
            {item.question}
          </h3>

          {/* +/- 图标 */}
          <div className="flex-shrink-0 w-6 h-6 flex items-center justify-center">
            {isExpanded ? (
              <svg
                className="w-5 h-5 text-[#0F1117]"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M20 12H4"
                />
              </svg>
            ) : (
              <svg
                className="w-5 h-5 text-[#0F1117]"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 4v16m8-8H4"
                />
              </svg>
            )}
          </div>
        </div>

        {/* 答案 - 展开时显示 */}
        <div
          className={`
            overflow-hidden transition-all duration-300
            ${isExpanded ? 'max-h-[500px] opacity-100 mt-4' : 'max-h-0 opacity-0'}
          `}
        >
          <div className="text-sm pad:text-base text-gray-600 leading-relaxed">
            {item.answer}
          </div>
        </div>
      </div>
    </div>
  );
};

const FAQSection: React.FC = () => {
  // 使用 Set 来跟踪展开的问题 ID，允许同时展开多个
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set(['faq-1']));

  const faqItems: FAQItem[] = [
    {
      id: 'faq-1',
      question: 'What is AI voice cloning?',
      answer:
        "AI voice cloning is a technology that recreates a person's voice from a short audio sample. It learns the tone, pitch, rhythm, and accent of your speech, then generates new audio that sounds almost indistinguishable from your real voice. With FalcoCut, you can instantly turn text into speech using your cloned voice—no studio time or re-recording required.",
    },
    {
      id: 'faq-2',
      question: 'How do I get started with FalcoCut?',
      answer: (
        <>
          <p className="mb-3">Getting started is simple:</p>
          <ol className="list-decimal pl-5 space-y-2">
            <li>Upload or record a clear audio sample (10–60 seconds works best).</li>
            <li>Let FalcoCut process it and build your personal voice profile.</li>
            <li>Type any text and hear it read out loud in your cloned voice.</li>
          </ol>
          <p className="mt-3">
            Even first-time users can create a professional-quality voice clone in
            minutes.
          </p>
        </>
      ),
    },
    {
      id: 'faq-3',
      question: 'Can I use cloned voices for commercial projects?',
      answer:
        'Yes. Paid plans on FalcoCut allow commercial use in ads, audiobooks, training, and more. Free users are limited to personal, non-commercial projects. Please remember: if you plan to clone voices other than your own, you must have clear rights and permission.',
    },
    {
      id: 'faq-4',
      question: 'Which languages are supported?',
      answer:
        'FalcoCut supports a growing list of languages, including English, Chinese (Mandarin), Japanese, Korean, Spanish, French, German, and more. This enables you to create content for global audiences without hiring multiple voice actors.',
    },
    {
      id: 'faq-5',
      question: 'What makes a good audio sample?',
      answer: (
        <>
          <p className="mb-3">For best results:</p>
          <ul className="list-disc pl-5 space-y-2">
            <li>Record in a quiet place without background noise.</li>
            <li>Use a clear, steady pace of speech.</li>
            <li>Ensure only one speaker is present in the recording.</li>
            <li>Avoid background music, echo, or distortion.</li>
          </ul>
          <p className="mt-3">
            Better input = more natural and accurate cloned voice output.
          </p>
        </>
      ),
    },
    {
      id: 'faq-6',
      question: 'Is AI voice cloning legal?',
      answer: (
        <>
          <p className="mb-3">
            Yes, cloning your own voice or voices you have rights to is fully legal.
            However:
          </p>
          <ul className="list-disc pl-5 space-y-2">
            <li>Impersonating others without consent is prohibited.</li>
            <li>Avoid uses that promote fraud, misinformation, or hate speech.</li>
            <li>
              Always disclose AI-generated voices where transparency is required (e.g.,
              marketing or public-facing content).
            </li>
          </ul>
          <p className="mt-3">
            FalcoCut enforces responsible AI usage and may restrict misuse.
          </p>
        </>
      ),
    },
    {
      id: 'faq-7',
      question: 'How realistic is the generated voice?',
      answer:
        "FalcoCut's models are trained on diverse, natural speech data to capture intonation, emotion, and rhythm. The result is highly lifelike audio—so natural that most listeners can't distinguish it from human speech.",
    },
    {
      id: 'faq-8',
      question: 'Is my data safe with FalcoCut?',
      answer:
        'Absolutely. All audio uploads are encrypted and used only for generating your voice clone. FalcoCut does not resell or misuse your data, and you remain in control of your voice model at all times. For enterprise users, we also provide privacy-first compliance with strict data handling standards.',
    },
    {
      id: 'faq-9',
      question: 'Can I download the cloned audio?',
      answer:
        'Yes. You can export your cloned voice as WAV files and use them in videos, podcasts, marketing materials, or training sessions.',
    },
    {
      id: 'faq-10',
      question: 'Are there usage limits?',
      answer: (
        <>
          <ul className="list-disc pl-5 space-y-2">
            <li>
              Free plan: limited characters/seconds per month, slower generation speeds,
              personal use only.
            </li>
            <li>
              Paid plans: higher limits, priority processing, and full commercial rights.
            </li>
          </ul>
          <p className="mt-3">
            This ensures flexibility whether you&apos;re testing the tool or producing at
            scale.
          </p>
        </>
      ),
    },
    {
      id: 'faq-11',
      question: 'What are the restrictions?',
      answer: (
        <>
          <p className="mb-3">FalcoCut prohibits:</p>
          <ul className="list-disc pl-5 space-y-2">
            <li>Cloning voices without consent.</li>
            <li>Impersonation for fraud, harassment, or misleading content.</li>
            <li>Harmful, illegal, or defamatory use.</li>
          </ul>
          <p className="mt-3">
            We are committed to making AI voice cloning safe, transparent, and empowering
            for all creators.
          </p>
        </>
      ),
    },
  ];

  const handleItemClick = (id: string) => {
    setExpandedIds((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(id)) {
        newSet.delete(id);
      } else {
        newSet.add(id);
      }
      return newSet;
    });
  };

  return (
    <section className="w-full bg-white py-12 pad:py-16 sm:py-20 px-4 pad:px-8 sm:px-[65px]">
      <div className="max-w-[1190px] mx-auto">
        {/* 标题 */}
        <h2 className="text-2xl pad:text-3xl sm:text-4xl font-bold text-[#0F1117] text-center mb-10 pad:mb-12 sm:mb-16">
          Frequently Asked Questions
        </h2>

        {/* FAQ 列表 - 840px 宽度居中 */}
        <div className="max-w-[840px] mx-auto flex flex-col gap-2">
          {faqItems.map((item) => (
            <FAQItemComponent
              key={item.id}
              item={item}
              isExpanded={expandedIds.has(item.id)}
              onClick={() => handleItemClick(item.id)}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

export default FAQSection;
